import React, { useState, useEffect, useMemo } from "react";
import PermissionButton from "../../shared/PermissionButton";
import Button from "../../shared/Button";
import {
  useEducationLevelByAssessmentModule,
  useEducationLevelDivisions,
  useYearLevelByEducationLevel,
} from "../../../hooks/useAssessment";
import {
  useSelectClassrooms,
  useStudentOptionalSubjects,
} from "../../../hooks/useOptionalSubjects";
import { Spinner } from "reactstrap";
import CustomModal from "../../shared/CustomModal";
import { checkRolePermissions } from "../../../../../utils/Constants";
import Select from "../../shared/Select";

const ListStudentOptionalSubject = ({ name }) => {
  const { educationLevel } = useEducationLevelByAssessmentModule();
  const { educationDivision, getEducationLevelDivision } =
    useEducationLevelDivisions();
  const { yearLevel, getYearLevelByEducationLevel } =
    useYearLevelByEducationLevel();
  const { classrooms, getClassrooms } = useSelectClassrooms();
  const {
    studentsOptionalSubject,
    loading,
    setStudentsOptionalSubject,
    selectStudentClassroomOptionalSubject,
    deleteStudentClassroomOptionalSubject,
  } = useStudentOptionalSubjects();

  const [unAssignModal, setUnAssignModal] = useState(false);
  const [formData, setFormData] = useState({
    educationLevelId: "",
    term: "",
    class: "",
    section: "",
    admissionId: "",
    subjectId: "",
  });
  const [permissions, setPermissions] = useState({
    permissionEditOptionalSubject: false,
    permissionDeleteOptionalSubject: false,
  });

  const checkPermissions = () => {
    setPermissions({
      permissionEditOptionalSubject: checkRolePermissions(
        "edit-optional-subject",
        "activity",
        name,
      ),
      permissionDeleteOptionalSubject: checkRolePermissions(
        "delete-optional-subject",
        "activity",
        name,
      ),
    });
  };

  const yearLevelOption = useMemo(() => {
    if (!yearLevel) return;
    return yearLevel?.map((item) => ({ label: item.name, value: item.id }));
  }, [yearLevel]);

  const handleUnAssignSubject = async () => {
    if (!formData?.admissionId || !formData?.subjectId) return;

    const params = {
      admissionId: formData.admissionId,
      classroomId: formData.section,
      educationLevelDivisionId: formData.term,
      educationLevelId: formData.educationLevelId,
      subjectId: formData.subjectId,
      yearLevelId: formData.class,
    };

    try {
      await deleteStudentClassroomOptionalSubject(params);
      setUnAssignModal(false);
    } catch (error) {
      console.error("UnAssign Failed", error);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  useEffect(() => {
    if (educationLevel?.length > 0) {
      const educationLevelId = educationLevel[0].value;
      setFormData((prev) => ({ ...prev, educationLevelId: educationLevelId }));
      getEducationLevelDivision(educationLevelId);
      getYearLevelByEducationLevel(educationLevelId);
    }
  }, [educationLevel]);

  return (
    <>
      <div className="arr-content">
        <div className="arr-heading">
          <h1>List Student Optional Subject</h1>
          <div className="arr-btns-group">
            <PermissionButton
              moduleName={"Subjects Setup"}
              widgetName={name}
              header={"List Student Optional Subject"}
              activityName={"select-student-optional-subjects"}
            />
          </div>
        </div>

        <form className="arr-form grid">
          <Select
            name={"educationDivision"}
            value={educationDivision?.find(
              (opt) => opt?.value === formData?.term,
            )}
            onChange={(option) => {
              setFormData((prev) => ({ ...prev, term: option?.value }));
              if (
                formData?.section !== "" &&
                formData?.class !== "" &&
                formData?.educationLevelId !== ""
              ) {
                const params = {
                  classroomId: formData?.section,
                  educationLevelDivisionId: option?.value,
                  educationLevelId: formData.educationLevelId,
                  yearLevelId: "",
                };

                selectStudentClassroomOptionalSubject(params);
                return;
              }
            }}
            options={educationDivision}
            placeholder={"Select Term"}
            isClearable={false}
          />

          <Select
            name={"class"}
            value={
              yearLevelOption?.find((opt) => opt?.value === formData?.class) ||
              null
            }
            options={yearLevelOption}
            onChange={(option) => {
              if (formData.class !== option?.value) {
                setFormData((prev) => ({ ...prev, class: option?.value, section: "" }));
                setStudentsOptionalSubject([]);
                getClassrooms(option?.value);
              }
            }}
            placeholder={"Select Class"}
            isDisabled={!formData.term}
            isClearable={false}
          />

          <Select
            name={"section"}
            value={
              classrooms?.find((opt) => opt?.value === formData?.section) ||
              null
            }
            options={classrooms}
            onChange={(option) => {
              setFormData((prev) => ({ ...prev, section: option?.value }));

              const params = {
                classroomId: option?.value,
                educationLevelDivisionId: formData.term,
                educationLevelId: formData.educationLevelId,
                yearLevelId: "",
              };

              selectStudentClassroomOptionalSubject(params);
            }}
            isDisabled={!formData?.class}
            placeholder={"Select Section"}
            isClearable={false}
          />
        </form>

        <div className="arr-table-container">
          <table aria-label="student-table" className="arr-table">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Optional Subject</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {loading.read ? (
                <tr>
                  <td colSpan={3}>
                    <div className="arr-spinner">
                      <div className="loader">
                        <Spinner size={"md"} color="primary"></Spinner>
                      </div>
                      <div style={{ fontSize: "13px", marginTop: "15px" }}>
                        Please wait !! Processing.....
                      </div>
                    </div>
                  </td>
                </tr>
              ) : studentsOptionalSubject?.length > 0 ? (
                studentsOptionalSubject?.map((item) => (
                  <tr key={item.admissionId}>
                    <td>{item?.studentName}</td>
                    <td>{item?.subjectName}</td>
                    <td>
                      {permissions.permissionDeleteOptionalSubject && (
                        <Button
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              admissionId: item?.admissionId,
                              subjectId: item?.subjectId,
                            }));
                            setUnAssignModal(true);
                          }}
                          size="sm"
                        >
                          Unassign
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} style={{ textAlign: "center" }}>
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CustomModal
        isOpen={unAssignModal}
        toggle={() => setUnAssignModal((prev) => !prev)}
        modalHeader={"Are you sure?"}
        children={
          <div>
            <p>You want to unassign this student?</p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                gap: "8px",
              }}
            >
              <Button
                variant="outline"
                onClick={() => setUnAssignModal((prev) => !prev)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={loading.delete}
                onClick={handleUnAssignSubject}
              >
                {loading.delete ? (
                  <Spinner size={"sm"} color="white" />
                ) : (
                  "Unassign"
                )}
              </Button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default ListStudentOptionalSubject;
